"use client";
import { AVATAR_SIZES } from "@cmp-src/components/UserAvatar/UserAvatar";
import { UserAvatarStack } from "@cmp-src/components/UserAvatar/UserAvatarStack";
import { ResourceLabel } from "@cmp-src/labels/ResourceLabel/ResourceLabel";
import type {
  DivisionsTree,
  GlobalSearchResourceItem,
  ISchedule,
  IUserFit,
  OfficeWithFloors
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import { useMemo } from "react";
import { normalizeDedicatedUsers } from "@/lib/dedicated-users";
import { GlobalSearchListItemLayout } from "../GlobalSearchListItemLayout";
import { getOfficeAndFloor } from "../globalSearchOfficeUtils";
import { getUsersFromSchedule } from "../globalSearchScheduleUtils";
import { useResourceScheduleInfo } from "../hooks/useScheduleInfo";

interface ResourceItemProps {
  resource: GlobalSearchResourceItem;
  schedules?: ISchedule[];
  isActive: boolean;
  onSelect: () => void;
  divisionCategories?: {
    offices: Record<string, OfficeWithFloors>;
    tree: DivisionsTree | null;
  };
}

export function ResourceItem({
  resource,
  isActive,
  schedules = [],
  onSelect,
  divisionCategories
}: ResourceItemProps) {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  const { officeWithFloors } = getOfficeAndFloor({
    officeId: resource.resource.office_id,
    parentId: resource.resource.parent_id,
    divisionCategories,
    userOfficesById: authenticatedUser?.offices?.byId
  });

  const scheduleTimeInfo = useResourceScheduleInfo(schedules, officeWithFloors);

  // Dedicated desks belong to one person — the availability chip is
  // replaced by who owns it (the desk is never bookable anyway). The
  // owner arrives in `dedicated_users` on the item; normalize defensively
  // and fall back to the visible assignment.
  const dedicatedUser: IUserFit | null = useMemo(() => {
    if (!resource.resource.is_dedicated) return null;
    const user =
      normalizeDedicatedUsers(resource.dedicated_users)[0] ??
      resource.resource.user_visible_assignments?.[0] ??
      null;
    if (!user) return null;
    return { roles: [], profile_photo_url: "", ...user };
  }, [resource]);
  const isDedicated = !!resource.resource.is_dedicated;

  // Schedule-caused unavailability reads as "Occupied" (with who's there)
  // instead of the generic "Not Available" — mirroring how the map treats
  // a reserved desk vs. one an admin disabled.
  const nonAvailableReason =
    typeof resource.non_available === "object" && resource.non_available
      ? resource.non_available.reason_code
      : undefined;
  // Reason object → the reason decides. Legacy boolean `true` carries no
  // reason — fall back to "did the search match schedules for it": if it
  // did, the unavailability is occupancy, not an admin lock.
  const isOccupiedBySchedule =
    !!resource.non_available &&
    (nonAvailableReason
      ? nonAvailableReason === "overlaps_with_schedule" ||
        nonAvailableReason === "buffer_overlaps_with_schedule" ||
        nonAvailableReason === "resource_over_capacity"
      : schedules.length > 0);

  // The occupancy text lives IN the chip (avatars + "Occupied until…"),
  // so the row doesn't repeat it in the header.
  const occupiedChipText = !scheduleTimeInfo
    ? "Occupied"
    : scheduleTimeInfo.type === "current"
      ? `Occupied until ${scheduleTimeInfo.endTime}`
      : scheduleTimeInfo.text;

  const occupyingUsers = useMemo<IUserFit[]>(() => {
    if (!schedules || schedules.length === 0) return [];

    const userMap = new Map<string, IUserFit>();

    for (const schedule of schedules) {
      const users = getUsersFromSchedule(schedule);
      for (const user of users) {
        userMap.set(user.id, user);
      }
    }

    return Array.from(userMap.values());
  }, [schedules]);

  // Past-window occupancy ("Was occupied …") only makes sense inside the
  // Occupied chip — next to a green Available chip it would contradict it.
  const scheduleHeader =
    scheduleTimeInfo && scheduleTimeInfo.type !== "past" ? (
      <div className="flex items-center gap-2 text-neutral-400 text-xs">
        {scheduleTimeInfo.type === "current" ? (
          <>
            {scheduleTimeInfo.users && scheduleTimeInfo.users.length > 0 ? (
              <div className="opacity-30 transition-all transition-duration-1000 hover:opacity-100">
                <UserAvatarStack
                  avatarSize={AVATAR_SIZES.ROUNDED_6_6}
                  users={scheduleTimeInfo.users}
                  hoverDirection="left"
                  maxVisibleAvatars={3}
                  showRest={false}
                />
              </div>
            ) : scheduleTimeInfo.isMeetingRoom &&
              scheduleTimeInfo.meetingTitle ? (
              <span className="max-w-md truncate">
                Meeting &quot;{scheduleTimeInfo.meetingTitle}&quot;
              </span>
            ) : (
              <span>Occupied</span>
            )}
            <span>until {scheduleTimeInfo.endTime}</span>
            {scheduleTimeInfo.freeUntilText && (
              <span>· {scheduleTimeInfo.freeUntilText}</span>
            )}
          </>
        ) : (
          <div className="flex items-center gap-2">
            <span>{scheduleTimeInfo.text}</span>
            <div className="opacity-30 transition-all transition-duration-1000 hover:opacity-100">
              {occupyingUsers.length > 0 && (
                <UserAvatarStack
                  avatarSize={AVATAR_SIZES.ROUNDED_6_6}
                  users={occupyingUsers}
                  hoverDirection="left"
                  maxVisibleAvatars={3}
                />
              )}
            </div>
          </div>
        )}
      </div>
    ) : undefined;

  return (
    <GlobalSearchListItemLayout
      isActive={isActive}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onSelect();
        }
      }}
      className={cn("gap-4 p-3")}
    >
      <ResourceLabel
        resource={resource.resource}
        showAmenities={false}
        allowFavorite={false}
        imageSize="sm"
        className="flex-1 min-w-0"
        headerRight={
          isDedicated || isOccupiedBySchedule ? undefined : scheduleHeader
        }
      />

      <div className="flex shrink-0 items-center gap-2">
        {isDedicated ? (
          <div className="flex items-center gap-1.5 rounded-[5px] border border-indigo-100 bg-indigo-50 px-1.5 py-1 font-inter font-medium text-[12px] text-primary">
            {dedicatedUser && (
              <UserAvatarStack
                avatarSize={AVATAR_SIZES.ROUNDED_6_6}
                users={[dedicatedUser]}
                maxVisibleAvatars={1}
                showRest={false}
              />
            )}
            Dedicated
          </div>
        ) : isOccupiedBySchedule ? (
          <div className="flex items-center gap-1.5 rounded-[5px] border border-neutral-100 px-1.5 py-1 font-inter font-medium text-[12px] text-neutral-1000">
            {occupyingUsers.length > 0 && (
              <UserAvatarStack
                avatarSize={AVATAR_SIZES.ROUNDED_6_6}
                users={occupyingUsers}
                maxVisibleAvatars={2}
                showRest={false}
              />
            )}
            <span className="max-w-52 truncate">{occupiedChipText}</span>
          </div>
        ) : (
          <div
            className={cn(
              "flex items-center gap-1.5 rounded-[5px] border border-neutral-100 px-1.5 py-1 font-inter font-medium text-[12px] text-neutral-1000",
              resource.non_available && "text-neutral-400"
            )}
          >
            {!resource.non_available && (
              <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-green-600" />
            )}
            {resource.non_available ? "Not Available" : "Available"}
          </div>
        )}
      </div>
    </GlobalSearchListItemLayout>
  );
}
